(() => {
  if (window.AuremeScrollRestoreUtils) return;

  const safeSessionStorage = {
    getItem(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (_error) {
        return null;
      }
    },

    setItem(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (_error) {
        // sessionStorage が使えない環境では通常のブラウザ挙動に任せる。
      }
    },

    removeItem(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (_error) {
        // sessionStorage が使えない環境では通常のブラウザ挙動に任せる。
      }
    },
  };

  /**
   * pathname と timestamp だけを持つ sessionStorage intent を検証済みの形へ変換する。
   * @param {string} rawIntent - sessionStorage から取得した値
   * @param {object} [options] - intent オプション
   * @param {(pathname: string) => string} [options.normalizePathname] - pathname 正規化処理
   * @returns {{ pathname: string, timestamp: number } | null}
   */
  function parsePathnameTimestampIntent(rawIntent, options = {}) {
    let intent;
    try {
      intent = JSON.parse(rawIntent);
    } catch (_error) {
      return null;
    }

    if (!intent || typeof intent !== 'object' || Array.isArray(intent)) {
      return null;
    }

    const { pathname, timestamp } = intent;
    if (
      typeof pathname !== 'string' ||
      typeof timestamp !== 'number' ||
      !Number.isFinite(timestamp)
    ) {
      return null;
    }

    const normalizedPathname =
      typeof options.normalizePathname === 'function'
        ? options.normalizePathname(pathname)
        : pathname;
    if (typeof normalizedPathname !== 'string') return null;

    return {
      pathname: normalizedPathname,
      timestamp,
    };
  }

  /**
   * pathname / timestamp intent を sessionStorage から読み出して消費する。
   * @param {string} key - sessionStorage key
   * @param {object} [options] - intent オプション
   * @param {(pathname: string) => string} [options.normalizePathname] - pathname 正規化処理
   * @returns {{ pathname: string, timestamp: number } | null}
   */
  function consumePathnameTimestampIntent(key, options = {}) {
    const rawIntent = safeSessionStorage.getItem(key);
    safeSessionStorage.removeItem(key);
    if (!rawIntent) return null;

    return parsePathnameTimestampIntent(rawIntent, options);
  }

  /**
   * pathname / timestamp intent が現在の対象として有効かを判定する。
   * @param {{ pathname: string, timestamp: number } | null} intent - 検証済み intent
   * @param {object} options - 判定オプション
   * @param {string} options.currentPathname - 現在の pathname
   * @param {number} options.ttlMs - 有効期限
   * @returns {boolean}
   */
  function isFreshPathnameTimestampIntent(intent, options = {}) {
    const { currentPathname, ttlMs } = options;
    if (
      !intent ||
      typeof intent.pathname !== 'string' ||
      typeof intent.timestamp !== 'number' ||
      !Number.isFinite(intent.timestamp) ||
      typeof currentPathname !== 'string' ||
      typeof ttlMs !== 'number' ||
      !Number.isFinite(ttlMs)
    ) {
      return false;
    }

    const isExpired = Date.now() - intent.timestamp > ttlMs;
    return !isExpired && intent.pathname === currentPathname;
  }

  /**
   * スクロール復元を一時的に手動へ切り替える。
   * @returns {() => void}
   */
  function temporarilyUseManualScrollRestoration() {
    if (!('scrollRestoration' in window.history)) {
      return () => {};
    }

    const previousScrollRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    return () => {
      window.history.scrollRestoration = previousScrollRestoration;
    };
  }

  /**
   * 初期描画や load の後勝ちに備えて、同じスクロール処理を複数回予約する。
   * @param {() => void} action - 繰り返し実行するスクロール処理
   * @param {object} options - 予約オプション
   * @param {number[]} options.delaysMs - setTimeout の遅延一覧
   * @param {boolean} options.includeAnimationFrame - rAF でも実行するか
   * @param {boolean} options.includeLoad - load 時にも実行するか
   * @returns {() => void}
   */
  function scheduleRepeatedScrollAction(action, options = {}) {
    if (typeof action !== 'function') return () => {};

    const { delaysMs = [], includeAnimationFrame = true, includeLoad = false } = options;
    let isCancelled = false;
    const runAction = () => {
      if (!isCancelled) action();
    };
    const timeoutIds = delaysMs.map((delay) => window.setTimeout(runAction, delay));
    const animationFrameId =
      includeAnimationFrame && typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(runAction)
        : null;

    if (includeLoad) {
      window.addEventListener('load', runAction, { once: true });
    }

    return () => {
      if (isCancelled) return;
      isCancelled = true;

      for (const timeoutId of timeoutIds) {
        window.clearTimeout(timeoutId);
      }
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (includeLoad) {
        window.removeEventListener('load', runAction);
      }
    };
  }

  /**
   * スクロール復元のユーザー操作キャンセルイベントをまとめて登録する。
   * @param {(event: Event) => void} cancelRestore - キャンセル処理
   * @param {object} options - イベントオプション
   * @param {string[]} options.events - pointer / wheel などのキャンセルイベント
   * @param {boolean} options.keydown - 任意の keydown でキャンセルするか
   * @param {(event: KeyboardEvent) => boolean} options.isCancelKey - キーキャンセル判定
   * @returns {() => void}
   */
  function bindScrollRestoreCancelEvents(cancelRestore, options = {}) {
    if (typeof cancelRestore !== 'function') return () => {};

    const { events = [], keydown = false, isCancelKey = null } = options;
    for (const eventName of events) {
      window.addEventListener(eventName, cancelRestore, { once: true, passive: true });
    }

    const shouldBindKeydown = keydown || typeof isCancelKey === 'function';
    const cancelOnKeydown = shouldBindKeydown
      ? (event) => {
          if (!isCancelKey || isCancelKey(event)) cancelRestore(event);
        }
      : null;

    if (cancelOnKeydown) {
      const keydownOptions =
        keydown && typeof isCancelKey !== 'function' ? { once: true } : undefined;
      window.addEventListener('keydown', cancelOnKeydown, keydownOptions);
    }

    return () => {
      for (const eventName of events) {
        window.removeEventListener(eventName, cancelRestore);
      }
      if (cancelOnKeydown) {
        window.removeEventListener('keydown', cancelOnKeydown);
      }
    };
  }

  /**
   * 手動スクロール復元の解除を、load または pagehide まで遅延する。
   * @param {() => void} restoreScrollRestoration - scrollRestoration を元へ戻す処理
   * @param {object} options - 解除オプション
   * @param {'after-load' | 'immediate'} options.mode - 解除タイミング
   * @param {() => void} options.onRelease - 解除後の通知
   * @returns {(() => void) | null}
   */
  function scheduleManualScrollRestorationRelease(restoreScrollRestoration, options = {}) {
    if (typeof restoreScrollRestoration !== 'function') return null;

    const { mode = 'after-load', onRelease = null } = options;
    if (mode === 'immediate' || document.readyState === 'complete') {
      restoreScrollRestoration();
      onRelease?.();
      return null;
    }

    let isReleased = false;

    function cleanup() {
      window.removeEventListener('load', release);
      window.removeEventListener('pagehide', release);
    }

    function release() {
      if (isReleased) return;

      isReleased = true;
      cleanup();
      restoreScrollRestoration();
      onRelease?.();
    }

    window.addEventListener('load', release, { once: true });
    window.addEventListener('pagehide', release, { once: true });

    return () => {
      if (isReleased) return;

      isReleased = true;
      cleanup();
    };
  }

  window.AuremeScrollRestoreUtils = {
    bindScrollRestoreCancelEvents,
    consumePathnameTimestampIntent,
    isFreshPathnameTimestampIntent,
    safeSessionStorage,
    scheduleManualScrollRestorationRelease,
    scheduleRepeatedScrollAction,
    temporarilyUseManualScrollRestoration,
  };
})();
